import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUsers, FiPlus, FiX, FiArrowLeft, FiTrash2, FiSearch, FiChevronDown, FiCheck } from 'react-icons/fi';
import { format } from 'date-fns';

const TeamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchTeamData();
    if (isAdmin) {
      fetchAvailableUsers();
    }
  }, [id, isAdmin]);

  const fetchTeamData = async () => {
    try {
      const [teamRes, tasksRes] = await Promise.all([
        api.get(`/teams/${id}`),
        api.get(`/tasks/team/${id}`),
      ]);
      setTeam(teamRes.data.team);
      setMembers(teamRes.data.members || []);
      setTasks(tasksRes.data.tasks || []);
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get(`/users/not-in-team/${id}`);
      setAvailableUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching available users:', error);
    }
  };

  const handleAddMember = async () => {
    if (selectedUsers.length === 0) return;

    try {
      // Add each selected user to the team
      for (const userId of selectedUsers) {
        await api.post(`/teams/${id}/members`, { user_id: parseInt(userId) });
      }
      
      const count = selectedUsers.length;
      showSuccess(
        `${count} member${count > 1 ? 's have' : ' has'} been added to the team! 🎉`,
        'Members Added!'
      );
      
      setShowAddMember(false);
      setSelectedUsers([]);
      setUserSearch('');
      fetchTeamData();
      fetchAvailableUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error adding members';
      showError(errorMsg, 'Failed to Add Members');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      await api.delete(`/teams/${id}/members/${userId}`);
      fetchTeamData();
      fetchAvailableUsers();
    } catch (error) {
      alert('Error removing member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="text-center py-12">
        <p className="text-white/80">Team not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/teams')}
        className="flex items-center space-x-2 text-white/80 hover:text-white transition-colors mb-4"
      >
        <FiArrowLeft size={20} />
        <span>Back to Teams</span>
      </motion.button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
      >
        <h1 className="text-3xl font-bold text-white mb-2">{team.name}</h1>
        {team.description && (
          <p className="text-white/70 mb-4">{team.description}</p>
        )}
        <div className="flex items-center space-x-4 text-sm text-white/60">
          <span className="flex items-center">
            <FiUsers size={16} className="mr-1" />
            {members.length} members
          </span>
        </div>
      </motion.div>

      {/* Members Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
          <h2 className="text-lg lg:text-xl font-bold text-white">Team Members</h2>
          {isAdmin && (
            <button
              onClick={() => setShowAddMember(!showAddMember)}
              className="bg-white text-primary-600 active:bg-white/90 px-5 py-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 shadow-lg touch-manipulation w-full sm:w-auto"
            >
              <FiPlus size={18} />
              <span>{showAddMember ? 'Cancel' : 'Add Member'}</span>
            </button>
          )}
        </div>

        {showAddMember && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-4 bg-white/10 rounded-lg border border-white/20"
          >
            {availableUsers.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-white/70 text-sm">All users are already members of this team.</p>
                <p className="text-white/50 text-xs mt-2">Create new users from the Users page to add them.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block text-white/90 text-sm font-medium mb-2">
                  Select a user to add to this team:
                </label>
                
                {/* Selected Users Display */}
                {selectedUsers.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {selectedUsers.map((userId) => {
                      const user = availableUsers.find(u => u.id.toString() === userId);
                      return user ? (
                        <span key={userId} className="inline-flex items-center px-3 py-2 rounded-full text-sm bg-green-500/20 text-green-200 font-medium">
                          {user.name}
                          <button
                            type="button"
                            onClick={() => setSelectedUsers(selectedUsers.filter(id => id !== userId))}
                            className="ml-2 text-green-200 hover:text-white font-bold"
                          >
                            ×
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Searchable Dropdown */}
                <div className="relative" ref={userDropdownRef}>
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      placeholder="Search users by name or email..."
                      className={`w-full pl-10 pr-10 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/50 ${
                        showUserDropdown ? 'rounded-b-none border-b-0' : ''
                      }`}
                    />
                    <FiChevronDown 
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 transition-transform ${
                        showUserDropdown ? 'rotate-180' : ''
                      }`} 
                      size={18}
                    />
                  </div>

                  <AnimatePresence>
                    {showUserDropdown && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute z-10 w-full bg-gray-800 border border-white/20 border-t-0 rounded-b-lg shadow-lg max-h-60 overflow-y-auto"
                      >
                        {(() => {
                          const filteredUsers = availableUsers.filter(user =>
                            user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
                            user.email.toLowerCase().includes(userSearch.toLowerCase())
                          );

                          if (filteredUsers.length === 0) {
                            return (
                              <div className="px-4 py-3 text-sm text-white/50 text-center">
                                No users found
                              </div>
                            );
                          }

                          return filteredUsers.map((user) => {
                            const isSelected = selectedUsers.includes(user.id.toString());
                            return (
                              <button
                                key={user.id}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedUsers(selectedUsers.filter(id => id !== user.id.toString()));
                                  } else {
                                    setSelectedUsers([...selectedUsers, user.id.toString()]);
                                  }
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center justify-between ${
                                  isSelected ? 'bg-green-500/20' : ''
                                }`}
                              >
                                <div>
                                  <span className={`block ${isSelected ? 'font-medium text-green-200' : 'text-white'}`}>
                                    {user.name}
                                  </span>
                                  <span className="text-xs text-white/50">{user.email}</span>
                                </div>
                                {isSelected && (
                                  <FiCheck className="text-green-400" size={18} />
                                )}
                              </button>
                            );
                          });
                        })()}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleAddMember}
                    disabled={selectedUsers.length === 0}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center"
                  >
                    Add {selectedUsers.length > 0 ? `${selectedUsers.length} Member${selectedUsers.length > 1 ? 's' : ''}` : 'to Team'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddMember(false);
                      setSelectedUsers([]);
                      setUserSearch('');
                    }}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-200 px-4 py-3 rounded-lg transition-all"
                  >
                    <FiX size={20} />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        <div className="space-y-2">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all"
            >
              <div>
                <p className="text-white font-medium">{member.name}</p>
                <p className="text-white/60 text-sm">{member.email}</p>
              </div>
              {isAdmin && member.role !== 'admin' && (
                <button
                  onClick={() => handleRemoveMember(member.id)}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  <FiTrash2 size={18} />
                </button>
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Tasks Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
      >
        <h2 className="text-xl font-bold text-white mb-4">Team Tasks</h2>
        {tasks.length === 0 ? (
          <p className="text-white/60 text-center py-8">No tasks for this team</p>
        ) : (
          <div className="space-y-3">
            {tasks.map((task) => (
              <Link
                key={task.id}
                to={`/tasks/${task.id}`}
                className="block bg-white/5 hover:bg-white/10 rounded-lg p-4 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">{task.title}</h3>
                    <p className="text-white/60 text-sm mb-2 line-clamp-2">
                      {task.description || 'No description'}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-white/50">
                      {task.assigned_to_name && (
                        <span>Assigned to: {task.assigned_to_name}</span>
                      )}
                      {task.due_date && (
                        <span>{format(new Date(task.due_date), 'MMM dd, yyyy')}</span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`${
                      task.status === 'completed' ? 'bg-green-500' :
                      task.status === 'in_progress' ? 'bg-blue-500' :
                      task.status === 'blocked' ? 'bg-red-500' : 'bg-gray-500'
                    } text-white text-xs px-2 py-1 rounded-full capitalize`}
                  >
                    {task.status.replace('_', ' ')}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default TeamDetail;
